import React from 'react';
import { getContext, getEasing, getDuration, isAnimationDisabled } from 'common';
import _merge from 'lodash/merge';
import { useSpring, animated } from 'react-spring';
import { SPEED_FAST } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { TRANSITION_DECELERATE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import { OPACITY_0 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/opacity';

import Styled from './styles';

const LaunchPadContext = React.createContext<any>({});

export interface LaunchPadProps extends React.ComponentPropsWithRef<'div'> {
  /** Place launchpad sub-components here. See sub-components and demo example for composition. */
  children: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** Control animation config here. This uses React-spring config. See https://react-spring.io for more information. */
  animationProps?: any;
  /** Set to true, the `LaunchPad` component will animate in. Use this in conjunction with `SignIn` components `animateOut` prop to animate between these two components. */
  animateIn?: boolean;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** @ignore */
  style?: React.CSSProperties
}

/** LaunchPad provides direct access to multiple apps. */
const LaunchPad = React.forwardRef<HTMLElement, LaunchPadProps>((props: LaunchPadProps, ref) =>
  getContext(({ RTL, theme, disableAnimation }) => {
    const {
      children, theme: themeProp, style, animateIn, animationProps, disableAnimation: propsdisableAnimation, ...otherProps
    } = props;
    const resolvedTheme = _merge({}, theme.LAUNCHPAD, themeProp);
    const resolvedDisabledAnimation = isAnimationDisabled(propsdisableAnimation, disableAnimation);
    const resolvedAnimateIn = animateIn && !(isAnimationDisabled(propsdisableAnimation, disableAnimation));
    const springProps = resolvedAnimateIn
      ? useSpring(animationProps || {
        to: [{ opacity: 1 }],
        from: { opacity: OPACITY_0 },
        config: {
          duration: getDuration(SPEED_FAST),
          easing: getEasing(TRANSITION_DECELERATE)
        },
        immediate: resolvedDisabledAnimation
      })
      : {};
    const resolvedStyles = _merge({}, springProps, style);
    return (
      <Styled.LaunchPad as={animated.div} theme={resolvedTheme} style={resolvedStyles} ref={ref} {...otherProps}>
        <LaunchPadContext.Provider value={{ animateIn: resolvedAnimateIn, disableAnimation: resolvedDisabledAnimation }}>
          <Styled.LaunchPadInner $rtl={RTL} theme={resolvedTheme}>
            {children}
          </Styled.LaunchPadInner>
        </LaunchPadContext.Provider>
      </Styled.LaunchPad>
    );
  }));

LaunchPad.displayName = 'LaunchPad';
LaunchPad.defaultProps = {
  theme: {},
  animationProps: undefined,
  disableAnimation: undefined,
  animateIn: false,
  style: {}
};

/* Exports */
export { LaunchPad as default, LaunchPadContext };
